import React, { useState, useEffect, useRef } from 'react';
import firebase from "firebase/app";
import "firebase/storage";
import * as XLSX from 'xlsx';
import TopMenu from '../TopMenu';
import ExamCodeSearchBar from './ExamCodeSearchBar';
import ExamSummaryCard from './ExamSummaryCard';
import ResultsTable from './ResultsTable';
import SubmissionDetailsAccordion from './SubmissionDetailsAccordion';
import {
  getMaxScore,
  getCorrectOptionIds,
  getCorrectOptionTexts,
  calculateSectionScore,
  getQuestionAnalysis,
  calculateTotalScore,
  formatSubmittedAt,
  stripHtmlTags
} from './utils';
import { Tooltip, Fab } from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';

const Results = () => {
  const [examCode, setExamCode] = useState('');
  const [examData, setExamData] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const detailsRef = useRef(null);

  const fetchExamResults = async () => {
    if (!examCode.trim()) {
      alert('Enter an assessment code to proceed');
      return;
    }
    setLoading(true);
    try {
      const examRef = firebase.database().ref(`TestPapers/${examCode}`);
      const examSnapshot = await examRef.once('value');
      const exam = examSnapshot.val();
      if (!exam) {
        alert('Assessment not found');
        setLoading(false);
        return;
      }
      const normalizedExam = {
        title: exam.title || exam.examTitle,
        isDynamic: exam.isDynamic || exam.dynamic,
        sections: exam.sections ? exam.sections.map(section => ({
          id: section.id,
          title: section.t || section.title,
          weightage: section.w || section.weightage,
          questions: section.q || section.questions ? (section.q || section.questions).map(q => ({
            id: q.id,
            type: q.type,
            text: q.text,
            maxScore: q.maxScore !== undefined ? Number(q.maxScore) : (q.ms !== undefined ? Number(q.ms) : 1),
            problemDescription: q.pd || q.problemDescription || '',
            instructions: q.inst || q.instructions || '',
            boilerplateCode: q.bc || q.boilerplateCode || '',
            evaluationKeywords: q.ek || q.evaluationKeywords || '',
            evaluationPoints: (() => {
                // Combine evaluationPoints and evaluationKeywords for backward compatibility
                const evalPoints = q.evaluationPoints || '';
                const evalKeywords = q.ek || q.evaluationKeywords || '';
                if (evalPoints && evalKeywords) {
                    // Both exist - combine them (remove duplicates)
                    const pointsArray = typeof evalPoints === 'string' 
                        ? evalPoints.split(',').map(p => p.trim()).filter(Boolean)
                        : Array.isArray(evalPoints) ? evalPoints : [];
                    const keywordsArray = typeof evalKeywords === 'string'
                        ? evalKeywords.split(',').map(k => k.trim()).filter(Boolean)
                        : Array.isArray(evalKeywords) ? evalKeywords : [];
                    const combined = [...pointsArray, ...keywordsArray];
                    return [...new Set(combined)].join(', ');
                }
                return evalPoints || evalKeywords || '';
            })(),
            enableAIEvaluation: q.ai !== undefined ? q.ai : (q.enableAIEvaluation || false),
            scenarioOptions: q.scenarioOptions || [],
            options: q.opts || q.options ? (q.opts || q.options).map(opt => ({
              id: opt.id,
              text: opt.t || opt.text,
              marks: opt.m !== undefined ? opt.m : (opt.marks || 0),
              isCorrect: opt.c !== undefined ? opt.c : (opt.isCorrect || false)
            })) : []
          })) : []
        })) : []
      };
      setExamData(normalizedExam);
      const submissionsRef = firebase.database().ref(`Exam_results/${examCode}`);
      const submissionsSnapshot = await submissionsRef.once('value');
      const submissionsData = submissionsSnapshot.val();
      if (submissionsData) {
        const submissionsArray = Object.entries(submissionsData).map(([email, data]) => ({
          email,
          ...data
        }));
        setSubmissions(submissionsArray);
      } else {
        setSubmissions([]);
      }
    } catch (error) {
      alert('Unable to retrieve assessment results');
    }
    setLoading(false);
  };

  const exportToExcel = async () => {
    if (!submissions.length) return;
    
    const hasSections = !!(examData.isDynamic && examData.sections);

    // Section scores per submission - computed once and reused for the headings and the rows
    const sectionScoresBySubmission = submissions.map(submission =>
      hasSections ? examData.sections.map(section => calculateSectionScore(section, submission)) : []
    );

    // Section headings carry the section's total score, e.g. "Aptitude (10)".
    // Built once up front so every row lands in the same column, even if a submission
    // is missing its AI evaluation and reports a different max score.
    const usedHeadings = new Set(['Name', 'Email', 'Submitted At', 'Total Score']);
    const sectionHeadings = hasSections ? examData.sections.map((section, index) => {
      const definedMaxScore = (section.questions || []).reduce(
        (total, q) => total + (q.maxScore !== undefined ? Number(q.maxScore) : 1), 0
      );
      const evaluatedMaxScore = sectionScoresBySubmission.reduce(
        (highest, sectionScores) => Math.max(highest, Number(sectionScores[index]?.maxScore) || 0), 0
      );
      const sectionMaxScore = evaluatedMaxScore || definedMaxScore;

      const name = section.title || `Section ${index + 1}`;
      let heading = `${name} (${sectionMaxScore})`;
      // Keep headings unique even when two sections share a title and a total
      if (usedHeadings.has(heading)) {
        heading = `${name} ${index + 1} (${sectionMaxScore})`;
      }
      usedHeadings.add(heading);
      return heading;
    }) : [];

    // Create summary data - scores only, one column per section plus the total
    const summaryData = submissions.map((submission, submissionIndex) => {
      // Calculate total scores using the proper function
      const totalScoreData = calculateTotalScore(examData, submission, calculateSectionScore);

      const row = {
        'Name': submission.name || 'N/A',
        'Email': submission.email,
        'Submitted At': formatSubmittedAt(submission.submittedAt)
      };

      sectionHeadings.forEach((heading, index) => {
        const sectionScore = sectionScoresBySubmission[submissionIndex][index] || {};
        const rawScore = sectionScore.rawScore ?? sectionScore.score ?? 0;
        row[heading] = Number(rawScore) || 0;
      });

      row['Total Score'] = Number(totalScoreData.totalScore) || 0;
      return row;
    });

    // Create detailed answers data
    const detailedAnswersData = [];
    
    // Process submissions sequentially to handle async URL generation
    for (const submission of submissions) {
      if (examData.isDynamic && examData.sections) {
        for (const [sectionIndex, section] of examData.sections.entries()) {
          for (const [questionIndex, question] of section.questions.entries()) {
            let questionDetail = null;
            let userAnswerText = '';
            let correctAnswerText = '';
            let userScore = 0;
            let maxScore = question.maxScore || 0;
            let status = 'Not Answered';
            let feedback = 'No evaluation available';
            let evaluationType = 'Direct Answer';
            
            // First try to get from AI evaluation
            const sectionScore = calculateSectionScore(section, submission);
            questionDetail = sectionScore.questionDetails.find(qd => 
              qd.questionText === question.text || 
              (question.id && qd.questionText && qd.questionText.includes(question.text?.substring(0, 50)))
            );
            
            if (questionDetail) {
              userAnswerText = stripHtmlTags(questionDetail.userAnswer || '');
              correctAnswerText = stripHtmlTags(questionDetail.correctAnswer || '');
              userScore = questionDetail.score || 0;
              maxScore = questionDetail.maxScore || 0;
              status = questionDetail.isCorrect ? 'Correct' : (questionDetail.score > 0 ? 'Partial' : 'Incorrect');
              feedback = stripHtmlTags(questionDetail.feedback || '');
              evaluationType = questionDetail.evaluationType || 'AI Evaluation';
            } else {
              // Fallback: get answer directly from submission.answers
              const directAnswer = submission.answers?.[question.id];
              if (directAnswer !== undefined && directAnswer !== null) {
                if (typeof directAnswer === 'string') {
                  userAnswerText = stripHtmlTags(directAnswer);
                } else if (typeof directAnswer === 'object') {
                  if (directAnswer.text) {
                    userAnswerText = stripHtmlTags(directAnswer.text);
                  } else if (directAnswer.code) {
                    userAnswerText = `Code: ${directAnswer.code}`;
                  } else if (directAnswer.videoUrl || directAnswer.videoPath) {
                    userAnswerText = `Video: ${directAnswer.videoUrl || directAnswer.videoPath}`;
                  } else {
                    userAnswerText = 'Answer provided (complex format)';
                  }
                } else {
                  userAnswerText = String(directAnswer);
                }
                status = 'Answered';
              } else {
                userAnswerText = 'Not answered';
                status = 'Not Answered';
              }
            }
            
            // Get video and PDF download URLs
            let videoDownloadUrl = '';
            let pdfDownloadUrl = '';
            const directAnswer = submission.answers?.[question.id];
            
            if (directAnswer && typeof directAnswer === 'object') {
              // Generate video download URL
              if (directAnswer.videoPath || directAnswer.videoUrl) {
                try {
                  if (directAnswer.videoPath) {
                    const fullPath = directAnswer.videoPath.startsWith('submissions/') 
                      ? directAnswer.videoPath 
                      : `submissions/${directAnswer.videoPath}`;
                    const storageRef = firebase.storage().ref(fullPath);
                    videoDownloadUrl = await storageRef.getDownloadURL();
                  } else if (directAnswer.videoUrl && directAnswer.videoUrl.startsWith('http')) {
                    videoDownloadUrl = directAnswer.videoUrl;
                  }
                } catch (error) {
                  console.error('Error getting video download URL:', error);
                  videoDownloadUrl = directAnswer.videoPath || directAnswer.videoUrl || 'URL not available';
                }
              }
              
              // Generate PDF download URL
              if (directAnswer.pdfFile) {
                try {
                  if (directAnswer.pdfFile.pdfPath) {
                    const fullPath = directAnswer.pdfFile.pdfPath.startsWith('submissions/') 
                      ? directAnswer.pdfFile.pdfPath 
                      : `submissions/${directAnswer.pdfFile.pdfPath}`;
                    const storageRef = firebase.storage().ref(fullPath);
                    pdfDownloadUrl = await storageRef.getDownloadURL();
                  } else if (directAnswer.pdfFile.url && directAnswer.pdfFile.url.startsWith('http')) {
                    pdfDownloadUrl = directAnswer.pdfFile.url;
                  }
                } catch (error) {
                  console.error('Error getting PDF download URL:', error);
                  pdfDownloadUrl = directAnswer.pdfFile.pdfPath || directAnswer.pdfFile.url || 'URL not available';
                }
              }
            }
            
            // Always add the question to detailed answers
            detailedAnswersData.push({
              'Name': submission.name || 'N/A',
              'Email': submission.email,
              'Section': section.title || `Section ${sectionIndex + 1}`,
              'Question #': questionIndex + 1,
              'Question Text': question.text || '',
              'Question Type': question.type || '',
              'User Answer': userAnswerText,
              'Correct Answer': correctAnswerText,
              'User Score': userScore,
              'Max Score': maxScore,
              'Status': status,
              'Feedback': feedback,
              'Evaluation Type': evaluationType,
              'Video Download URL': videoDownloadUrl,
              'PDF Download URL': pdfDownloadUrl
            });
          }
        }
      }
    }
    
    console.log('Detailed answers data length:', detailedAnswersData.length);
    console.log('Sample detailed answer:', detailedAnswersData[0]);

    // Create workbook with multiple sheets
    const wb = XLSX.utils.book_new();
    
    // Add summary sheet
    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");
    
    // Add detailed answers sheet (always create, even if empty)
    const detailedWs = XLSX.utils.json_to_sheet(detailedAnswersData);
    XLSX.utils.book_append_sheet(wb, detailedWs, "Detailed Answers");
    
    XLSX.writeFile(wb, `${examCode}_results.xlsx`);
  };

  // Scroll to details section when a submission is selected
  useEffect(() => {
    if (selectedSubmission && detailsRef.current) {
      setTimeout(() => {
        detailsRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [selectedSubmission]);

  // Handle scroll to show/hide scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleSetSelectedSubmission = (submission) => {
    setSelectedSubmission(submission);
  };

  return (
    <div style={{ paddingTop: 70, paddingLeft: 8, paddingRight: 8, maxWidth: '100%', margin: '0 auto', background: '#f7f9fb', minHeight: '100vh' }}>
      <TopMenu showHome showLogout />
      <ExamCodeSearchBar
        examCode={examCode}
        setExamCode={setExamCode}
        fetchExamResults={fetchExamResults}
        loading={loading}
      />
      {examData && (
        <ExamSummaryCard
          examData={examData}
          examCode={examCode}
          submissions={submissions}
          exportToExcel={exportToExcel}
        />
      )}
      {submissions.length > 0 && (
        <ResultsTable
          examData={examData}
          submissions={submissions}
          selectedSubmission={selectedSubmission}
          setSelectedSubmission={handleSetSelectedSubmission}
          calculateSectionScore={calculateSectionScore}
        />
      )}
      {selectedSubmission && examData && (
        <div ref={detailsRef}>
          <SubmissionDetailsAccordion
            examData={examData}
            selectedSubmission={selectedSubmission}
            setSelectedSubmission={setSelectedSubmission}
            calculateSectionScore={calculateSectionScore}
          />
        </div>
      )}
      {showScrollTop && (
        <Tooltip title="Scroll to top" placement="left">
          <Fab
            color="primary"
            size="small"
            onClick={scrollToTop}
            style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000,
              background: '#1976d2',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
            }}
          >
            <ArrowUpwardIcon />
          </Fab>
        </Tooltip>
      )}
    </div>
  );
};

export default Results; 